import { useEffect, useState } from 'react'
import { api } from '../api/client.js'

/**
 * Notification template row as returned by
 *   GET /admin/conversations/notification-templates
 * (apps/api/src/routes/conversations/admin/notification-templates.ts).
 */
export interface NotificationTemplate {
  id: string
  eventKey: string
  audience: 'agent' | 'customer'
  channels: string[]
  bodyTemplate: string
  isActive: boolean
}

export interface UseNotificationTemplatesFilter {
  audience?: 'agent' | 'customer'
}

export interface UseNotificationTemplatesReturn {
  templates: NotificationTemplate[]
  loading: boolean
  error: string | null
}

// Module-level stable empty fallback so consumers don't see a fresh []
// reference each render (zustand selector / inline ?? [] infinite-loop
// pitfall analog for hook return values).
const EMPTY_TEMPLATES: NotificationTemplate[] = []

/**
 * Fetches notification templates from
 * GET /admin/conversations/notification-templates[?audience=…].
 *
 * Per §0.C: applies a defensive client-side filter on `audience` in case
 * the backend ignores the query, and always filters out inactive templates
 * (`isActive=true`).
 */
export function useNotificationTemplates(
  filter?: UseNotificationTemplatesFilter,
): UseNotificationTemplatesReturn {
  const [templates, setTemplates] = useState<NotificationTemplate[]>(EMPTY_TEMPLATES)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const audience = filter?.audience

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)
    const qs = audience ? `?audience=${audience}` : ''
    api
      .get<{ rows: NotificationTemplate[] }>(
        `/admin/conversations/notification-templates${qs}`,
      )
      .then((resp) => {
        if (cancelled) return
        const rows = resp.rows ?? EMPTY_TEMPLATES
        // Defensive client-side filter in case the backend ignores ?audience.
        const byAudience = audience
          ? rows.filter((t) => t.audience === audience)
          : rows
        const active = byAudience.filter((t) => t.isActive)
        setTemplates(active.length === 0 ? EMPTY_TEMPLATES : active)
      })
      .catch((e: Error) => {
        if (!cancelled) {
          setError(e.message)
          setTemplates(EMPTY_TEMPLATES)
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [audience])

  return { templates, loading, error }
}
